<template>
  <template v-if="useIsMobile().value">
    <!-- bg-color="#DD3131" -->
    <KHeaderBar title-text="Flash Sales" ft-color="#fff" bg-img="https://image.kilimall.com/lite/new_wap/bg_flash_sale_head_a.png" >
      <template #right>
        <KSShareStoreShareButton
          shareImageUrl="https://img.kilimall.com/c/h5/imgs/share-flashsale.png"
          shareTitle="🌟 Hey friend! You’ve got to see these incredible flash sale offers on Kilimall! 🛒💥 Hurry, they won’t last long! Tap in before they’re gone!🕒💖"
          sourceType="Flashsale"
        />
      </template>
    </KHeaderBar>
    <div class="flash-sales">        
        <div class="times" id="wap__flash-sales__tabs">
          <div 
            v-for="(item, idx) in flashSalesTime"
            :key="idx"
            :id="`wap__flash-sales-tab__${item.id}`"
            :class="item.act ? 'time-item time-item_act' : 'time-item'"
            @click="handleTimeClick(item)"
          >
            <p class="time-label">{{ item.label }}</p>
            <p class="status-label">
              {{ useFlashSalesStatusMap().value[item.status] }}
            </p>
          </div>
        </div>
      <!-- </div> -->
      <div class="sku-categorys">
        <div
          v-for="item in categoryList"
          :key="item.id"
          :class="skuParams.categoryId === item.id 
            ? 'category-item act-category-item'
            : 'category-item'
          "
          @click="handleCategoryClick(item.id)"
        >
          {{ item.name  }}
        </div>
      </div>

      <div class="countdown-box" v-if="currentIFSTime?.status !== 4">
        <p class="cd-title">
          <span v-if="currentIFSTime?.status === 1">Closing in:</span>
          <span v-else>Starting in:</span>
        </p>
        <div class="countdown">
          <span class="cd-time">{{ countdown.h }}</span>&nbsp;:&nbsp;
          <span class="cd-time">{{ countdown.m }}</span>&nbsp;:&nbsp;
          <span class="cd-time">{{ countdown.s }}</span>
        </div>
      </div>
      
      <div class="skus-wrapper">
        <van-skeleton :loading="loading && skuList.length <= 0" row="6">
          <van-list
            :loading="loading"
            :finished="finished"
            finished-text="To load more"
            @load="onLoad"  
          >          
            <div
              v-for="item in skuList"
              :key="item.skuId"
              class="sku"
              @click="handleToDetailClick(item)"
            >
              <KProductFlashSalesCard :flash-sku="item" :status="currentIFSTime?.status || 1" />
            </div>
          </van-list>

          <template #template>
            <div style="width: 100%;">
              <div
                v-for="i in 5"
                :key="i"
                style="display: flex; background-color: #fff;margin-bottom: 10px;"
              >
                <van-skeleton-image />
                <div :style="{ flex: 1, marginLeft: '16px' }">
                  <van-skeleton-paragraph row-width="60%" />
                  <van-skeleton-paragraph />
                  <van-skeleton-paragraph />
                  <van-skeleton-paragraph />
                </div>
              </div>
            </div>
          </template>
        </van-skeleton>
      </div>
    </div>
  </template>

  <FlashSales
    v-else
    :flashSalesTime="flashSalesTime"
    :countdown="countdown"
    :skus="skuList"
    :finished="finished"
    :loading="loading"
    :categoryList="categoryList"
    :selectCategoryId="skuParams.categoryId"
    :act-status="currentIFSTime?.status || 1"
    :flash-sale-session-label="currentIFSTime?.label || ''"
    :flash-sale-category-name="currentCategoryName"
    @time:click="handleTimeClick"
    @load:more="onLoad"
    @category:click="handleCategoryClick"
  />
</template>

<script setup lang="ts">
import { getFlashSalesConfig, getFlashSkus, getFlashCategoryById } from '@/api/models/flash-sales'
import { onMounted } from 'vue'
import { useRouter } from 'vue-router'
import FlashSales from '@/pages-pc/FlashSales.vue';
import { buildFlashSaleListingSource, type IToListingQuery } from '~/plugins/utils';

definePageMeta({ layout: 'wap' })

const headers = useRequestHeaders()
const proto = headers['x-forwarded-proto'] || 'https'
const host = `${proto}://${process.server ? headers.host : window.location.host}`
useHead({
  link: [
    {
      rel: "canonical",
      href: `${host}/flash-sales`
    }
  ],
  meta: [
    { content: `${host}/flash-sales?source=Share|flashSale|`, property: 'og:url' },
    { content: 'Flash Sales', property: 'og:title' },
    { content: 'https://img.kilimall.com/c/h5/imgs/share_flashsale_bg@2x.png', property: 'og:image' },
    { content: 'website', property: 'og:type' },
    { content: 'Discover great bargains on Kilimall\'s flash sale channel, featuring top-quality products at unbeatable prices.', property: 'og:description' }
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "name": "Flash Sales Breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": host
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Flash Sales",
            "item": `${host}/flash-sales`
          }
        ]
      })
    }
  ]
})

export interface ICountdown { h: string, m: string, s: string }
export interface IFSTime {
  id: number
  label: string
  status: number
  endTime: string,
  startTime: string
  act: boolean,
  queryTime: string
}
export interface ICategory {
  /**
   * 类目图标URL
   */
  iconUrl?: null | string;
  /**
   * 前端类目ID
   */
  id: number;
  /**
   * 前端类目名称
   */
  name: string;
}
const { $tz, $toListingDetail } = useNuxtApp()
const router = useRouter()
const route = useRoute()
const flashSalesTime = ref<IFSTime[]>([])
const countdown = reactive<ICountdown>({ h: '00', m: '00', s: '00' })
let interval: NodeJS.Timer | null = null
const initCountdown = (endDate: string, currentDate: string) => {
  if(interval) {
    return
  }

  const endTime = new Date(endDate).getTime() / 1000
  let currentTime = new Date(currentDate).getTime() / 1000
  
  interval = setInterval(() => {
    const _t: number = endTime - currentTime

    let hour = parseInt(`${_t / (60 * 60) % 24}`)
    let minute = parseInt(`${_t / 60 % 60}`)
    let second = parseInt(`${_t % 60}`)

    currentTime += 1

    countdown.h = hour < 10 ? `0${hour}` : `${hour}`
    countdown.m = minute < 10 ? `0${minute}` : `${minute}`
    countdown.s = second < 10 ? `0${second}` : `${second}`
  }, 1000)
}
const mounted = async() => {
  const { sessions = [] } = await getFlashSalesConfig()
  
  sessions.forEach((c: any, index: number) => {
    const { startTime, endTime, status, queryTime } = c
    const startT = $tz.zeroToLocalZonetime(startTime, { format: 'hh:mm' })

    // if(status === 1) {
    //   initCountdown(endTime, queryTime)
    // }

    flashSalesTime.value.push({ 
      id: c.id,
      endTime,
      startTime,
      label: startT,
      status,
      act: status === 1,
      queryTime
    })
  })
}
onMounted(async() => {
  await mounted()

  const actItem = flashSalesTime.value.find((c: IFSTime) => c.act)
  
  await handleTimeClick(actItem || flashSalesTime.value[0])
})

watchEffect(
  async() => {
    const actItem = flashSalesTime.value.find((c: IFSTime) => c.act)
    
    if(actItem) {
      await nextTick()
      
      const el = document.getElementById(`wap__flash-sales-tab__${actItem.id}`) as HTMLDivElement
      const tabsEl = document.getElementById('wap__flash-sales__tabs') as HTMLDivElement
      if(tabsEl && el) {
        const width21 = el.offsetWidth / 2
        const tabsW = tabsEl.offsetWidth / 2
        const offsetLeft = el.offsetLeft + width21 - tabsW
        
        tabsEl.scrollLeft = offsetLeft
      }
    }
  }
)

const skuParams = reactive({ lastId: -1, limit: 18, categoryId: 0, skip: 0 })
const skuList = ref<any>([])
const categoryList = ref<ICategory[]>([])

const initSkus = async(type?: string, isLoading?:boolean) => {
  try {
    loading.value = true
    const id = flashSalesTime.value.find((c: IFSTime) => c.act)?.id
    if(!id) {
      // 没有活动
      finished.value = true
      return
    }
    // const { skus } = await getFlashSkus(id, skuParams, isLoading)
    const { skus } = await getFlashSkus(id, skuParams, false)
    // || useIsMobile().value
    finished.value = skus.length < skuParams.limit
    
    if(type === 'refresh') {
      skuList.value = skus
    } else {
      skuList.value.push(...skus)
    }
  } catch(e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

const currentIFSTime = ref<IFSTime>()
const handleTimeClick = async(item: IFSTime) => {
  clearInterval(interval)
  interval = null
  if(item.status === 1) {
    initCountdown(item.endTime, item.queryTime)
  } else if(item.status !== 4) { 
    initCountdown(item.startTime, new Date())
  } else {
    countdown.h = '00'
    countdown.m = '00'
    countdown.s = '00'
  }

  flashSalesTime.value.forEach((c: IFSTime) => {
    c.act = item.id === c.id
  })
  // 切换时间段，重新初始化分类
  skuParams.categoryId = 0
  skuParams.skip = 0
  skuParams.lastId = -1

  const [{ categories }] = await Promise.all([
    getFlashCategoryById(item.id),
    initSkus('refresh', true)
  ])

  categoryList.value = categories || []
  // 有商品分类才添加 All 选项
  categoryList.value.unshift({ name: 'All', id: 0 })

  skuParams.categoryId = 0

  currentIFSTime.value = item
}
const handleCategoryClick = async(id: number) => {
  skuParams.categoryId = id
  skuParams.skip = 0
  skuParams.lastId = -1

  await initSkus('refresh', true)
}
const loading = ref<boolean>(true)
const finished = ref<boolean>(false)
const onLoad = async() => {
  if(loading.value || finished.value) {
    return
  }

  skuParams.lastId = skuList.value[skuList.value.length - 1]?.activityId
  skuParams.skip += skuParams.limit
  await initSkus()
}

const currentCategoryName = computed(() =>
  categoryList.value.find((c: ICategory) => c.id === skuParams.categoryId)?.name || 'All'
)

const handleToDetailClick = (item: any) => {
  const { /** image, activityId */ title, shortTitle, skuId,  } = item

  const params: IToListingQuery = {
    source: buildFlashSaleListingSource(
      currentIFSTime.value?.label || '',
      currentCategoryName.value,
      'home',
      route
    ),
    title,
    shortTitle
  }

  currentIFSTime.value?.status === 1 ? (params.from = 'flash-sale') : (params.type = 'sku')

  $toListingDetail(skuId, params)
}
</script>

<style lang="scss" scoped>
.flash-sales{
  padding-top: 88px;
}
.times{
  height: 180px;
  flex: 1;
  font-size: 28px;
  display: flex;
  background-color: #ff532e;
  align-items: center;
  overflow: auto hidden;
  padding: 10px 20px;
  background-image: url('https://image.kilimall.com/lite/new_wap/bg_flash_sale_head_b.png');
  background-size: 100% 100%;
  background-repeat: no-repeat;
  position: sticky;
  z-index: 2;
  top: 2px;
  &::-webkit-scrollbar{
    display: none;
  }
  .status-label{
    font-size: 22px;
    margin-top: 20px;
  }
  .time-label{
    font-size: 28px;
    padding: 12px 30px;
    font-size: 32px;
    border-radius: 20px;
  }
  .time-item{
    border-radius: 10px;
    text-align: center;
    color: #fff;
    font-size: 24px;
    padding: 15px;
    white-space: nowrap;
    transition: all .2s;
  }
  .time-item_act {
    .time-label{
      color: var(--van-primary-color);
      background-color: #fff;
      display: inline-block;
      text-shadow: 0 0px 1px var(--van-primary-color);
    }
  }
}
.sku-categorys{
  display: flex;
  width: 100%;
  height: 100px;
  background-color: #fff;
  padding: 20px 30px;
  overflow-x: auto;
  box-shadow: 0 0 12px #cacaca;
  position: sticky;
  top: 155px;
  z-index: 500;
  &::-webkit-scrollbar{
    display: none;
  }
  .category-item{
    margin-right: 20px;
    white-space: nowrap;
    padding: 16px 30px;
    background-color: #efefef;
    color: #666;
    border-radius: 6px;
    &:last-child{
      margin-right: 0;
    }
  }
  .act-category-item{
    background: linear-gradient(to right, #ff7004, #ef5542);
    color: #fff;
  }
}
.countdown-box{
  display: flex;
  align-items: flex-end;
  justify-content: end;
  // height: 40px;
  margin: 20px;
  .cd-title{
    margin-right: 10px;
    color: #999;
    font-size: 28px;
    text-shadow: 0 0 1px #999;
  }
  .countdown{
    display: flex;
    align-items: center;
    .cd-time{
      padding: 5px;
      font-size: 22px;
      border-radius: 4px;
      background-color: var(--default_color);
      color: #fff;
    }
  }
}
.skus-wrapper::v-deep(.van-skeleton){
  padding: 0 !important;
}
.skus-wrapper{
  background-color: #fff;
  padding: 30px;
  .sku-item{
    display: flex;
    align-items: center;
    .image{
      width: 160px;
      min-width: 160px;
      position: relative;
      img{
        width: 160px;
        height: 160px;
        object-fit: contain;
      }
      .discount{
        position: absolute;
        top: 0;
        left: 0;
        color: #fff;
        background: linear-gradient(135deg,#ffba00,#ff6c47 100%,#ff6047 0);
        border-radius: 8px;
        font-size: 22px;
        padding: 4px 8px;
      }
    }
    .content{
      font-size: 28px;
      flex: 1;
      height: 160px;
      box-sizing: border-box;
      padding: 20px;
      padding-right: 0;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      .p-title{
        line-height: 32px;
        height: 64px;
        @include ellipsisMultiline(2);
      }
      .price-box{
        display: flex;
        align-items: center;
        justify-content: space-between;
        .label{
          color: #666;
          font-size: 24px;
        }
        .price-val{
          span{display: inline-block;}
          border-radius: 80px;
          overflow: hidden;
          font-size: 28px;
          .val{
            padding: 6px 25px 6px 25px;
            color: #fff;
            background-color: var(--main_color);
          }
          .go{
            padding: 6px 15px 6px 0;
            background-color: #ffba00;
            color: var(--default_color);
            position: relative;
            font-weight: 600;
            &::before{
              position: absolute;
              left: -27px;
              top: 0;
              content: "";
              border-style: solid;
              border-color: #ffba00;
              border-width: 28px;
              border-left-width: 14px;
              border-right-width: 15px;
              border-left-color: transparent;
              border-top-color: transparent;
            }
          }
        }
      }
    }
  }
}
</style>